// The prop library (tools/art/props.py → public/models/props.glb): loaded once, cloned per use.
// Materials are shared; `accent`, `accent_glow` and `liquid` are re-tinted per room or seat.
import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader.js';
import { toToon } from './materials';

export type PropName =
  | 'desk'
  | 'chair'
  | 'bookshelf'
  | 'bookshelf_low'
  | 'filing_cabinet'
  | 'filing_cabinet_tall'
  | 'plant'
  | 'tree'
  | 'tree_small'
  | 'cypress'
  | 'bush'
  | 'hedge'
  | 'lamp_post'
  | 'candelabra'
  | 'column'
  | 'statue'
  | 'banner'
  | 'lectern'
  | 'summoning'
  | 'alchemy'
  | 'pylon'
  | 'stacks'
  | 'crystal_ball'
  | 'enchant_desks'
  | 'stamp_counter'
  | 'fountain'
  | 'shop'
  | 'judge_bench'
  | 'planning_board'
  | 'meeting_table'
  | 'bench'
  | 'brazier'
  | 'cauldron'
  | 'candles'
  | 'crystals'
  | 'tesla'
  | 'orrery'
  | 'globe'
  | 'card_catalog'
  | 'book_pile'
  | 'furnace'
  | 'barrels'
  | 'crates'
  | 'display_case'
  | 'pigeonholes'
  | 'pipes'
  | 'sconce'
  | 'rune_lantern'
  | 'easel'
  | 'topiary'
  | 'inkwell'
  | 'coin_dish'
  | 'scroll'
  | 'meeple_quill'
  | 'meeple_key'
  | 'meeple_star'
  | 'meeple_scroll'
  | 'meeple_lantern'
  | 'meeple_intern';

export interface Tint {
  accent?: string;
  glow?: string;
  liquid?: string;
  /** A seat's contrast trim (hat band, belt, feather, emblem). */
  trim?: string;
}

const TINTABLE = new Set(['accent', 'accent_shade', 'accent_glow', 'liquid', 'trim']);
/** `accent_shade` is the accent darkened to about 70% in sRGB (colours here are linear). */
const SHADE = 0.5;

export class PropLibrary {
  private protos = new Map<string, THREE.Object3D>();
  private tinted = new Map<string, THREE.Material>();

  constructor(root: THREE.Object3D) {
    const converted = new Map<string, THREE.Material>();
    root.traverse((o) => {
      if (o.name.startsWith('prop_') && o.parent === root) this.protos.set(o.name.slice(5), o);
      const m = o as THREE.Mesh;
      if (m.isMesh) {
        m.castShadow = true;
        m.receiveShadow = true;
        const mats = materials(m).map((src) => {
          let out = converted.get(src.uuid);
          if (!out) {
            prepare(src as THREE.MeshStandardMaterial);
            out = toToon(src as THREE.MeshStandardMaterial);
            converted.set(src.uuid, out);
          }
          return out;
        });
        m.material = Array.isArray(m.material) ? mats : mats[0]!;
      }
    });
  }

  has(name: string): boolean {
    return this.protos.has(name);
  }

  /** A clone of a prop. Geometry is shared; tinted materials are cached per colour. */
  make(name: PropName, tint: Tint = {}): THREE.Object3D {
    const proto = this.protos.get(name);
    if (!proto) return new THREE.Group();
    const o = proto.clone(true);
    o.position.set(0, 0, 0);
    o.traverse((c) => {
      const m = c as THREE.Mesh;
      if (!m.isMesh) return;
      const mats = materials(m).map((mat) => this.tint(mat as THREE.MeshToonMaterial, tint));
      m.material = Array.isArray(m.material) ? mats : mats[0]!;
    });
    return o;
  }

  private tint(mat: THREE.MeshToonMaterial, t: Tint): THREE.Material {
    if (!TINTABLE.has(mat.name)) return mat;
    const color =
      mat.name === 'accent' || mat.name === 'accent_shade'
        ? t.accent
        : mat.name === 'trim'
          ? t.trim
          : mat.name === 'accent_glow'
            ? (t.glow ?? t.accent)
            : (t.liquid ?? t.glow ?? t.accent);
    if (!color) return mat;
    const key = `${mat.name}:${color}`;
    let out = this.tinted.get(key) as THREE.MeshToonMaterial | undefined;
    if (!out) {
      out = mat.clone();
      out.color.set(color);
      if (mat.name === 'accent_shade') out.color.multiplyScalar(SHADE);
      else if (mat.name === 'accent_glow' || mat.name === 'liquid') {
        out.emissive.set(color);
        out.color.set(color).multiplyScalar(0.6);
      }
      this.tinted.set(key, out);
    }
    return out;
  }
}

function materials(m: THREE.Mesh): THREE.Material[] {
  return Array.isArray(m.material) ? m.material : [m.material];
}

function prepare(mat: THREE.MeshStandardMaterial): void {
  if (!mat || (mat as unknown as { __prepared?: boolean }).__prepared) return;
  (mat as unknown as { __prepared?: boolean }).__prepared = true;
  mat.envMapIntensity = 0.7;
  if (mat.name === 'glass') {
    mat.transparent = true;
    mat.opacity = 0.35;
    mat.depthWrite = false;
    mat.roughness = 0.05;
  }
  if (mat.name === 'accent_glow' || mat.name === 'liquid' || mat.name === 'flame' || mat.name === 'glow_warm' || mat.name.startsWith('potion_') || mat.name === 'lamp_green') {
    // A few restrained magical lights; only flames are allowed to bloom.
    const level: Record<string, number> = { flame: 2.4, glow_warm: 0.55, lamp_green: 0.45, liquid: 0.7, accent_glow: 0.9 };
    mat.emissiveIntensity = level[mat.name] ?? (mat.name.startsWith('potion_') ? 0.2 : 0.8);
  }
}

let loading: Promise<PropLibrary> | null = null;
let loaded: PropLibrary | null = null;

export function loadProps(): Promise<PropLibrary> {
  if (!loading) {
    const draco = new DRACOLoader();
    draco.setDecoderPath(`${import.meta.env.BASE_URL}draco/`);
    const loader = new GLTFLoader();
    loader.setDRACOLoader(draco);
    loading = loader.loadAsync(`${import.meta.env.BASE_URL}models/props.glb`).then((gltf) => {
      draco.dispose();
      loaded = new PropLibrary(gltf.scene);
      return loaded;
    });
  }
  return loading;
}

export function props(): PropLibrary | null {
  return loaded;
}
